import './Markets.less';

import React, { FC, memo } from 'react';
import { dispatch, useReframe } from 'nike-re-framejs';
import { localizeMap } from '../utils';
import { overviewMarket$$, selectedMarketIds$$ } from '../push/overview/subs';
import { setSelectedMarketIds } from '../handlers';
import clsx from 'clsx';
import { MarketSelection } from './MarketSelection';
import { useMatchMedia } from '../hooks/useMatchMedia';
import { ErrorBoundary } from '../../../common/ErrorBoundary';
import useMarketIds from '../hooks/useMarketIds';
import { isMobileChannel } from '../../../common/util';

const MARKET_CLASSNAMES = 'flex-1 turbo-live-col';
export const getMarketId = (sportName, columnId, index) => `mh-${sportName}-${columnId}-${index}`;

const MarketHeader: FC<{ marketHeader: any; index; columnId; sportName: string }> = ({
    marketHeader,
    index,
    columnId,
    sportName
}) => {
    const matchMedia = useMatchMedia();
    const isMobileView = isMobileChannel();
    return (
        <span className="p-rel market-col flex-1 flex">
            <span
                id={getMarketId(sportName, columnId, index)}
                className={clsx('market-col-inner ellipsis fs-10 flex-1', {
                    'market-col-inner-mini': !matchMedia.xxl && !isMobileView
                })}
                title={localizeMap(marketHeader.get('headerInfoText'))}
            >
                {localizeMap(marketHeader.get('header'))}
            </span>
        </span>
    );
};

const MarketDummy = () => {
    return <div className={MARKET_CLASSNAMES} />;
};

export const Market: FC<{
    marketId: string;
    sportName: string;
    onSelect: (marketId: string) => void;
    otherClass?: string;
    columnId: number;
    isFirst?: boolean;
}> = memo(({ marketId, sportName, onSelect, otherClass, columnId, isFirst }) => {
    const derefSub = useReframe();
    const market = derefSub(overviewMarket$$(sportName, marketId));

    const mobileChannel = isMobileChannel();
    return market ? (
        <ErrorBoundary description="TurboLive Market">
            <div
                className={clsx(MARKET_CLASSNAMES, otherClass, {
                    'ml-20': mobileChannel && !isFirst
                })}
            >
                <MarketSelection marketId={marketId} onSelect={onSelect} sportName={sportName} columnId={columnId} />

                <div className="flex text-center">
                    {market.get('headers')?.map((marketHeader, i) => (
                        <MarketHeader
                            marketHeader={marketHeader}
                            key={i}
                            sportName={sportName}
                            index={i}
                            columnId={columnId}
                        />
                    ))}
                </div>
            </div>
        </ErrorBoundary>
    ) : isFirst && isMobileChannel() ? (
        <MarketDummy />
    ) : (
        <div className={MARKET_CLASSNAMES} />
    );
});

const Markets: FC<{ sportCode: string }> = memo(({ sportCode }) => {
    const derefSub = useReframe();
    const visibleMarketIds = useMarketIds(sportCode);
    const selectedMarketIds = derefSub(selectedMarketIds$$(sportCode));
    const onMarketSelected = index => marketId =>
        dispatch(setSelectedMarketIds(sportCode, selectedMarketIds.set(index, marketId)));
    return visibleMarketIds?.size ? (
        visibleMarketIds.map((marketId, i) => (
            <Market
                sportName={sportCode}
                marketId={marketId}
                onSelect={onMarketSelected(i)}
                key={i}
                columnId={i}
                isFirst={i === 0}
            />
        ))
    ) : (
        <div className="flex-1" />
    );
});

export default Markets;
